import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { buildFargoReport, toFargoCsv } from '../../utils/fargoReport';
import { updateTournament, writeInstanceBracketDiff } from '../../firebase/tournaments';

const FARGO_EMAIL = 'support@fargorate.com';

const isDoublesFormat = (f) => f === 'Doubles' || f === 'Scotch Doubles';

/**
 * "Report to Fargo" modal. Assembles a completed tournament/instance's singles
 * matches into a FargoRate-friendly report: a results table, a downloadable CSV,
 * and a pre-filled email to support@fargorate.com (Fargo has no API). Players with
 * no Fargo id are included as NEW so Fargo can create them.
 *
 * Props: { tournament, instanceData, instanceDate, onClose }
 */
export default function ReportToFargoModal({ tournament, instanceData, instanceDate, onClose }) {
  const source = instanceData || tournament;
  const doubles = isDoublesFormat(tournament.playerFormat);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(!doubles);
  const [error, setError] = useState('');
  const [reportedAt, setReportedAt] = useState(source.reportedToFargo || null);
  const [showEmail, setShowEmail] = useState(false);
  const [copiedField, setCopiedField] = useState('');

  const shortDate = instanceDate ? instanceDate.split('T')[0] : '';
  const bracketUrl = `${window.location.origin}/tournament/${tournament.shareUrl}${shortDate ? `?d=${shortDate}` : ''}`;
  const eventName = tournament.title || 'Tournament';
  const reportDate = shortDate || (tournament.scheduledDateTime ? String(tournament.scheduledDateTime).split('T')[0] : '');

  const emailSubject = `Pool tournament results for FargoRate — ${eventName}`;
  const emailBody =
    `Hello,\n\nPlease add the following pool tournament results to FargoRate.\n\n` +
    `Event: ${eventName}\nDate: ${reportDate}\nPublic bracket: ${bracketUrl}\n\n` +
    `The results spreadsheet (CSV downloaded from pooltournaments.app) is attached. It lists ` +
    `each match's games won and players' FargoRate membership IDs. Players marked "NEW" are ` +
    `not yet in FargoRate — please create them from these results (contact info included ` +
    `where available).\n\nThank you.`;

  useEffect(() => {
    if (doubles) return;
    let active = true;
    (async () => {
      try {
        const r = await buildFargoReport(source, tournament, {
          eventName,
          date: reportDate,
          gameType: tournament.gameType,
          winnersRace: tournament.winnersRaceLength || tournament.raceLength,
          losersRace: tournament.losersRaceLength || tournament.raceLength,
          bracketUrl,
        });
        if (active) setReport(r);
      } catch (e) {
        if (active) setError('Could not assemble the report. Please try again.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markReported = async () => {
    const iso = new Date().toISOString();
    setReportedAt(iso);
    try {
      if (instanceDate) {
        // Occurrence data lives in the subcollection doc now.
        await writeInstanceBracketDiff(tournament.id, instanceDate, {
          metadata: { reportedToFargo: iso },
          docExists: true,
        });
      } else {
        await updateTournament(tournament.id, { reportedToFargo: iso });
      }
    } catch (e) { /* best-effort flag — ignore */ }
  };

  const downloadCsv = () => {
    if (!report) return;
    const csv = toFargoCsv(report.rows);
    const safe = `${eventName}-${reportDate || 'results'}`.replace(/[^a-z0-9-]+/gi, '_');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fargo-report-${safe}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    markReported();
  };

  const revealEmail = () => {
    setShowEmail(true);
    markReported();
  };

  const copyField = async (field, text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for older/insecure contexts where the Clipboard API is unavailable.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* nothing more we can do */ }
      document.body.removeChild(ta);
    }
    setCopiedField(field);
    setTimeout(() => setCopiedField((f) => (f === field ? '' : f)), 1500);
  };

  const emailFields = [
    { key: 'to', label: 'To', value: FARGO_EMAIL, multiline: false },
    { key: 'subject', label: 'Subject', value: emailSubject, multiline: false },
    { key: 'body', label: 'Body', value: emailBody, multiline: true },
  ];

  // Render into document.body so the fixed overlay covers the whole viewport and
  // isn't trapped inside an ancestor with a transform (e.g. the sliding sidebar).
  return createPortal(
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: 'var(--text-primary)' }}>📤 Report to FargoRate</h2>
          <button onClick={onClose} style={xBtn} title="Close">×</button>
        </div>

        {reportedAt && (
          <div style={{ fontSize: 12, color: 'var(--success-text)', marginBottom: 10 }}>
            ✓ Marked reported on {new Date(reportedAt).toLocaleString()}
          </div>
        )}

        {doubles ? (
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            FargoRate ratings are individual, so <strong>doubles / scotch-doubles</strong> tournaments
            aren't reportable. Run singles tournaments to report results to Fargo.
          </p>
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>Assembling results…</div>
        ) : error ? (
          <div style={{ color: 'var(--danger)', padding: 16 }}>{error}</div>
        ) : report && report.matchCount === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>No completed singles matches with scores to report yet.</p>
        ) : report && (
          <>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginTop: 0 }}>
              FargoRate has no automated submission. <strong>Download the CSV and email it to {FARGO_EMAIL}</strong>,
              and/or send them your public bracket link. Players without a Fargo ID are included as <strong>NEW</strong>
              {' '}so Fargo can establish them.
            </p>

            <div style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: 10 }}>
              <strong>{eventName}</strong>{reportDate ? ` · ${reportDate}` : ''} — {report.matchCount} matches
              {report.newPlayers.length > 0 && ` · ${report.newPlayers.length} new player${report.newPlayers.length > 1 ? 's' : ''}`}
            </div>

            <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 8, marginBottom: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg-tertiary)' }}>
                    <th style={th}>Player 1</th><th style={th}>Score</th><th style={th}>Player 2</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((r, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border-light)' }}>
                      <td style={td}>{r.p1Name}{r.p1New && <span style={newTag}>NEW</span>}</td>
                      <td style={{ ...td, textAlign: 'center', fontWeight: 700 }}>{r.p1Games}–{r.p2Games}</td>
                      <td style={td}>{r.p2Name}{r.p2New && <span style={newTag}>NEW</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {report.newPlayers.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, padding: '8px 10px', backgroundColor: 'var(--warning-bg)', borderRadius: 6 }}>
                <strong>New players</strong> (Fargo will create from these results):{' '}
                {report.newPlayers.map((p) => `${p.name}${p.registered ? '' : ' (walk-in)'}`).join(', ')}.
                {report.newPlayers.some((p) => !p.email && !p.phone) && ' Some have no contact info — add it before sending if you can.'}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={downloadCsv} style={primaryBtn}>⬇ Download CSV</button>
              <button onClick={revealEmail} style={secondaryBtn}>✉ Email FargoRate</button>
            </div>

            {showEmail && (
              <div style={emailPanel}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.5 }}>
                  FargoRate has no upload form — copy these into an email to <strong>{FARGO_EMAIL}</strong> and
                  {' '}<strong>attach the CSV</strong> you downloaded.
                </div>
                {emailFields.map((f) => (
                  <div key={f.key} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{f.label}</span>
                      <button onClick={() => copyField(f.key, f.value)} style={copyBtn}>
                        {copiedField === f.key ? '✓ Copied' : 'Copy'}
                      </button>
                    </div>
                    {f.multiline ? (
                      <textarea readOnly value={f.value} rows={8} onFocus={(e) => e.target.select()} style={fieldBox} />
                    ) : (
                      <input readOnly value={f.value} onFocus={(e) => e.target.select()} style={fieldBox} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

const overlay = { position: 'fixed', inset: 0, backgroundColor: 'var(--bg-modal-overlay)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflow: 'auto' };
const modal = { backgroundColor: 'var(--bg-secondary)', borderRadius: 12, padding: 20, width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-xl)' };
const xBtn = { background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: 'var(--text-secondary)', lineHeight: 1 };
const th = { textAlign: 'left', padding: '8px 10px', color: 'var(--text-secondary)', fontWeight: 700 };
const td = { padding: '8px 10px', color: 'var(--text-primary)' };
const newTag = { marginLeft: 6, fontSize: 10, fontWeight: 700, color: 'var(--warning-text)', backgroundColor: 'var(--warning-bg)', padding: '1px 5px', borderRadius: 4 };
const primaryBtn = { padding: '10px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, backgroundColor: 'var(--accent-primary)', color: '#fff' };
const secondaryBtn = { padding: '10px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' };
const emailPanel = { marginTop: 14, padding: 14, border: '1px solid var(--border-color)', borderRadius: 8, backgroundColor: 'var(--bg-tertiary)' };
const copyBtn = { padding: '3px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700, backgroundColor: 'var(--accent-primary)', color: '#fff', border: 'none' };
const fieldBox = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 12, fontFamily: 'inherit', resize: 'vertical' };
